import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { CheckCircle, Circle, Loader } from 'lucide-react';
import './PDFPageSelector.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFPageSelectorProps {
    file: File;
    selectedPages: number[];
    disabledPages?: number[];
    onSelectionChange: (pages: number[]) => void;
    onTotalPagesChange?: (totalPages: number) => void;
    mode?: 'single' | 'multiple' | 'range' | 'view';
}

const PDFPageSelector: React.FC<PDFPageSelectorProps> = ({
    file,
    selectedPages,
    disabledPages = [],
    onSelectionChange,
    onTotalPagesChange,
    mode = 'multiple',
}) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const [rangeStart, setRangeStart] = useState<number | null>(null);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setIsLoading(false);
        // Notify parent component of total pages
        if (onTotalPagesChange) {
            onTotalPagesChange(numPages);
        }
    };

    const onDocumentLoadError = (error: Error) => {
        console.error('Error loading PDF:', error);
        setIsLoading(false);
    };

    const handlePageClick = (pageNumber: number) => {
        if (mode === 'view') return;
        if (disabledPages.includes(pageNumber)) return;

        if (mode === 'single') {
            onSelectionChange([pageNumber]);
            return;
        }

        if (mode === 'range' && rangeStart !== null) {
            // Complete range selection
            const start = Math.min(rangeStart, pageNumber);
            const end = Math.max(rangeStart, pageNumber);
            const range = Array.from({ length: end - start + 1 }, (_, i) => start + i)
                .filter(p => !disabledPages.includes(p)); // Filter out disabled pages

            // Merge with existing selection
            const newSelection = [...new Set([...selectedPages, ...range])].sort((a, b) => a - b);
            onSelectionChange(newSelection);
            setRangeStart(null);
        } else if (mode === 'range') {
            // Start range selection
            setRangeStart(pageNumber);
            if (!selectedPages.includes(pageNumber)) {
                onSelectionChange([...selectedPages, pageNumber].sort((a, b) => a - b));
            }
        } else {
            // Toggle selection for multiple mode
            if (selectedPages.includes(pageNumber)) {
                onSelectionChange(selectedPages.filter(p => p !== pageNumber));
            } else {
                onSelectionChange([...selectedPages, pageNumber].sort((a, b) => a - b));
            }
        }
    };

    const selectAll = () => {
        const allPages = Array.from({ length: numPages }, (_, i) => i + 1)
            .filter(p => !disabledPages.includes(p));
        onSelectionChange(allPages);
    };

    const clearSelection = () => {
        onSelectionChange([]);
        setRangeStart(null);
    };

    const formatSelectedPages = (): string => {
        if (selectedPages.length === 0) return '未选择';

        // Convert to range format (e.g., "1-3, 5, 7-9")
        const ranges: string[] = [];
        let start = selectedPages[0];
        let end = selectedPages[0];

        for (let i = 1; i <= selectedPages.length; i++) {
            if (i < selectedPages.length && selectedPages[i] === end + 1) {
                end = selectedPages[i];
            } else {
                if (start === end) {
                    ranges.push(`${start}`);
                } else {
                    ranges.push(`${start}-${end}`);
                }
                if (i < selectedPages.length) {
                    start = selectedPages[i];
                    end = selectedPages[i];
                }
            }
        }

        return ranges.join(', ');
    };

    const isViewMode = mode === 'view';

    return (
        <div className="pdf-page-selector">
            {!isViewMode && (
                <div className="selector-header">
                    <div className="selection-info">
                        <span className="selection-count">
                            已选择 <strong>{selectedPages.length}</strong> / {numPages - disabledPages.length} 页 (可选)
                        </span>
                        <span className="selection-range">
                            {formatSelectedPages()}
                        </span>
                    </div>
                    <div className="selection-actions">
                        <button
                            type="button"
                            className="btn-text"
                            onClick={selectAll}
                        >
                            全选可选
                        </button>
                        <button
                            type="button"
                            className="btn-text"
                            onClick={clearSelection}
                            disabled={selectedPages.length === 0}
                        >
                            清空
                        </button>
                    </div>
                </div>
            )}

            {mode === 'range' && rangeStart !== null && (
                <div className="range-hint">
                    <span>📍 范围选择模式：起始页 {rangeStart}，点击另一页完成范围选择</span>
                    <button
                        type="button"
                        className="btn-text-small"
                        onClick={() => setRangeStart(null)}
                    >
                        取消
                    </button>
                </div>
            )}

            <div className="pages-container">
                {isLoading && (
                    <div className="loading-state">
                        <Loader className="spinner" />
                        <span>加载 PDF 中...</span>
                    </div>
                )}

                <Document
                    file={file}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading=""
                >
                    <div className="pages-grid">
                        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => {
                            const isSelected = selectedPages.includes(pageNumber);
                            const isDisabled = disabledPages.includes(pageNumber);
                            const isRangeStart = rangeStart === pageNumber;

                            return (
                                <div
                                    key={pageNumber}
                                    className={`page-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isRangeStart ? 'range-start' : ''}`}
                                    onClick={() => handlePageClick(pageNumber)}
                                    style={isViewMode ? { cursor: 'default', pointerEvents: 'none' } : {}}
                                >
                                    <div className="page-preview">
                                        <Page
                                            pageNumber={pageNumber}
                                            width={150}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                        {isDisabled && (
                                            <div className="disabled-overlay">
                                                <span>已分配</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="page-footer" style={isViewMode ? { justifyContent: 'center' } : {}}>
                                        <span className="page-number">第 {pageNumber} 页</span>
                                        {!isViewMode && (
                                            <div className="page-checkbox">
                                                {isDisabled ? null : (
                                                    isSelected ? (
                                                        <CheckCircle className="icon-checked" />
                                                    ) : (
                                                        <Circle className="icon-unchecked" />
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Document>
            </div>
        </div>
    );
};

export default PDFPageSelector;
